import type { ComponentProps } from 'react';
import React, { createContext, useContext, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@change/ds-utils';

import { forwardRef } from '@change-corgi/core/react/core';

type PromptInputContextType = {
	isLoading: boolean;
	value: string;
	setValue: (value: string) => void;
	maxHeight: number | string;
	onSubmit?: () => void;
	disabled?: boolean;
	textareaRef: React.MutableRefObject<HTMLTextAreaElement | null>;
};

const PromptInputContext = createContext<PromptInputContextType>({
	isLoading: false,
	value: '',
	setValue: () => {},
	maxHeight: 240,
	onSubmit: undefined,
	disabled: false,
	textareaRef: { current: null },
});

function usePromptInput() {
	return useContext(PromptInputContext);
}

export type PromptInputProps = {
	isLoading?: boolean;
	value?: string;
	onValueChange?: (value: string) => void;
	maxHeight?: number | string;
	onSubmit?: () => void;
	children: React.ReactNode;
	className?: string;
	disabled?: boolean;
} & React.ComponentProps<'div'>;

function PromptInput({
	className,
	isLoading = false,
	maxHeight = 240,
	value,
	onValueChange,
	onSubmit,
	children,
	disabled = false,
	onClick,
	...props
}: PromptInputProps) {
	const [internalValue, setInternalValue] = useState(value || '');
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const handleChange = (newValue: string) => {
		setInternalValue(newValue);
		onValueChange?.(newValue);
	};

	return (
		<PromptInputContext.Provider
			data-qa="prompt-input"
			value={{
				isLoading,
				value: value ?? internalValue,
				setValue: onValueChange ?? handleChange,
				maxHeight,
				onSubmit,
				disabled,
				textareaRef,
			}}
		>
			<div
				data-qa="prompt-input-wrapper"
				className={cn(
					'elevation-elevated bg-background-base rounded-large relative flex overflow-hidden border border-stroke-strong',
					'has-[textarea:focus]:outline-stroke-focus has-[textarea:focus]:border-transparent has-[textarea:focus]:outline has-[textarea:focus]:outline-2',
					className,
				)}
				{...props}
			>
				{children}
			</div>
		</PromptInputContext.Provider>
	);
}

const Textarea = forwardRef<HTMLTextAreaElement, ComponentProps<'textarea'>>(({ className, ...props }, ref) => {
	return (
		<textarea ref={ref} data-qa="prompt-input-textarea" data-slot="textarea" className={cn('', className)} {...props} />
	);
});

export type PromptInputTextareaProps = {
	disableAutosize?: boolean;
} & React.ComponentProps<typeof Textarea>;

function PromptInputTextarea({
	className,
	onKeyDown,
	disableAutosize = false,
	onChange,
	...props
}: PromptInputTextareaProps) {
	const { value, setValue, maxHeight, onSubmit, disabled, textareaRef } = usePromptInput();

	const adjustHeight = (el: HTMLTextAreaElement | null) => {
		if (!el || disableAutosize) return;

		// eslint-disable-next-line no-param-reassign
		el.style.height = 'auto';

		if (typeof maxHeight === 'number') {
			// eslint-disable-next-line no-param-reassign
			el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
		} else {
			// eslint-disable-next-line no-param-reassign
			el.style.height = `min(${el.scrollHeight}px, ${maxHeight})`;
		}
	};

	const handleRef = (el: HTMLTextAreaElement | null) => {
		textareaRef.current = el;
		adjustHeight(el);
	};

	useLayoutEffect(() => {
		if (!textareaRef.current || disableAutosize) return;

		const el = textareaRef.current;
		el.style.height = 'auto';

		if (typeof maxHeight === 'number') {
			el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
		} else {
			el.style.height = `min(${el.scrollHeight}px, ${maxHeight})`;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value, maxHeight, disableAutosize]);

	const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		adjustHeight(e.target);
		setValue(e.target.value);
		onChange?.(e);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			onSubmit?.();
		}
		onKeyDown?.(e);
	};

	return (
		<Textarea
			data-qa="prompt-input-textarea"
			ref={handleRef}
			value={value}
			onChange={handleChange}
			onKeyDown={handleKeyDown}
			className={cn(
				'min-h-[44px] w-full resize-none border-none px-5 pb-16 pt-6 text-size-body shadow-none outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
				className,
			)}
			rows={1}
			disabled={disabled}
			{...props}
		/>
	);
}

export type PromptInputActionsProps = React.HTMLAttributes<HTMLDivElement>;

function PromptInputActions({ children, className, ...props }: PromptInputActionsProps) {
	return (
		<div className={cn('absolute bottom-3 right-3 flex items-center gap-2', className)} {...props}>
			{children}
		</div>
	);
}

export { PromptInput, PromptInputTextarea, PromptInputActions };
